// Pure helpers for the Loans comparison tab (LoanComparison.jsx).
const fixed = (digits) => (params) => (
  params.value === null || params.value === undefined || Number.isNaN(Number(params.value))
    ? ''
    : Number(params.value).toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
);

const dateOnly = (params) => (params.value ? String(params.value).slice(0, 10) : '');

// Tape columns shown for every loan (fields match the /loans response).
export const LOAN_COLUMNS = [
  { field: 'LOAN_ID_PRIMARY', headerName: 'Loan ID', pinned: 'left', minWidth: 130 },
  { field: 'TRUST', headerName: 'Trust', enableRowGroup: true },
  { field: 'LOAN_PROGRAM_GROUP', headerName: 'Program', enableRowGroup: true },
  { field: 'INSURANCE_ENTITY', headerName: 'Entity', enableRowGroup: true, maxWidth: 110 },
  { field: 'CURRENT_LOAN_AMOUNT', headerName: 'Balance', type: 'numericColumn', valueFormatter: fixed(0) },
  { field: 'LOAN_RATE_CURRENT', headerName: 'Coupon', type: 'numericColumn', valueFormatter: fixed(3), maxWidth: 110 },
  { field: 'LOAN_RATE_SERVICING', headerName: 'Servicing', type: 'numericColumn', valueFormatter: fixed(3), maxWidth: 110 },
  { field: 'LOAN_AMORT_TYPE', headerName: 'Amort', maxWidth: 100 },
  { field: 'LOAN_ARM_INDEX_TYPE', headerName: 'Index', maxWidth: 120 },
  { field: 'PROPERTY_STATE', headerName: 'State', maxWidth: 90 },
  { field: 'LOAN_CREDIT_SCORE', headerName: 'FICO', type: 'numericColumn', maxWidth: 90 },
  { field: 'LOAN_LTV_ORIG', headerName: 'LTV', type: 'numericColumn', valueFormatter: fixed(1), maxWidth: 90 },
  { field: 'DELQ_STATUS_MBA', headerName: 'Status', maxWidth: 100 },
  { field: 'LOAN_MATURITY_DT', headerName: 'Maturity', valueFormatter: dateOnly, maxWidth: 120 },
];

// Price columns: Clearwater for positions, the tape's optional PRICE for uploads.
export const POSITION_PRICE_COLUMNS = [
  { field: 'BOOK_PRICE', headerName: 'Book Price', type: 'numericColumn', valueFormatter: fixed(3) },
  { field: 'MARKET_PRICE', headerName: 'Market Price', type: 'numericColumn', valueFormatter: fixed(3) },
];
export const UPLOAD_PRICE_COLUMNS = [
  { field: 'PRICE', headerName: 'Tape Price', type: 'numericColumn', valueFormatter: fixed(3) },
];

// parseFileData returns Excel date cells as Dates at local midnight; the mapper reads
// ISO dates. coerceDate formats in UTC, which east of UTC lands on the previous day
// (a 1st-of-month due date becomes the 31st), so format the local calendar date.
const localIsoDate = (value) => (
  Number.isNaN(value.getTime())
    ? null
    : [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, '0'),
      String(value.getDate()).padStart(2, '0'),
    ].join('-')
);

export const normalizeUploadRows = (rows = []) => rows.map((row) => Object.fromEntries(
  Object.entries(row).map(([key, value]) => [key, value instanceof Date ? localIsoDate(value) : value]),
));

// The scenario report calibrates a loan's spread to a price, so a loan run targets a price only.
const TARGET_LABELS = {
  price: (value) => `Price ${value}`,
};

export const describeRun = (run, curveLabel = run.curve_scenario) => {
  let curve = 'Base curve';
  if (run.shock_bps) curve = `${run.shock_bps > 0 ? '+' : ''}${run.shock_bps}bps shock`;
  else if (run.curve_scenario) curve = `Curve ${curveLabel}`;
  const [kind, value] = Object.entries(run.target || {})[0] || [];
  return kind ? `${curve} · ${TARGET_LABELS[kind](value)}` : curve;
};

// Enforced again server-side: a run prices off a named curve scenario, a shock, or
// neither (the base curve), never both.
export const runProblem = (run, runs = []) => {
  if (!run.label?.trim()) return 'Give the run a label';
  if (runs.some((other) => other !== run && other.label === run.label)) return 'Labels must be unique';
  if (run.curve_scenario && run.shock_bps) return 'Choose a curve scenario or a rate shock, not both';
  if (!Number.isInteger(Number(run.shock_bps || 0))) return 'Shock must be whole basis points';
  const [kind, value] = Object.entries(run.target || {})[0] || [];
  if (kind && (value === null || value === undefined || !Number.isFinite(Number(value)))) {
    return 'Enter a number for the target';
  }
  if (kind === 'price' && Number(value) <= 0) return 'Price target must be positive';
  return null;
};
